import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
  Query,
  CollectionReference
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Order, OrderStatus } from '../models/Order';

// Collection name in Firestore
const COLLECTION_NAME = 'orders';

/**
 * Add a new order to Firestore
 * @param orderData Order data to add
 * @returns The ID of the newly created order
 */
export const addOrder = async (orderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    // Convert Date objects to Firestore Timestamps
    const dataWithTimestamps = {
      ...orderData,
      startDate: orderData.startDate ? Timestamp.fromDate(orderData.startDate) : null,
      expectedEndDate: orderData.expectedEndDate ? Timestamp.fromDate(orderData.expectedEndDate) : null,
      actualEndDate: orderData.actualEndDate ? Timestamp.fromDate(orderData.actualEndDate) : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), dataWithTimestamps);
    return docRef.id;
  } catch (error) {
    console.error('Error adding order:', error);
    throw error;
  }
};

/**
 * Get all orders from Firestore
 * @param limitCount Optional limit to the number of results
 * @returns Array of orders
 */
export const getAllOrders = async (limitCount?: number): Promise<Order[]> => {
  try {
    let q: Query | CollectionReference = collection(db, COLLECTION_NAME);
    
    // Apply ordering and limit if specified
    if (limitCount) {
      q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));
    } else {
      q = query(q, orderBy('createdAt', 'desc'));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to Date objects
        startDate: data.startDate ? data.startDate.toDate() : new Date(),
        expectedEndDate: data.expectedEndDate ? data.expectedEndDate.toDate() : undefined,
        actualEndDate: data.actualEndDate ? data.actualEndDate.toDate() : undefined,
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt ? data.updatedAt.toDate() : new Date()
      } as Order;
    });
  } catch (error) {
    console.error('Error getting orders:', error);
    throw error;
  }
};

/**
 * Get orders by status
 * @param status Order status to filter by
 * @param limitCount Optional limit to the number of results
 * @returns Array of orders with the specified status
 */
export const getOrdersByStatus = async (status: OrderStatus, limitCount?: number): Promise<Order[]> => {
  try {
    let q: Query | CollectionReference = query(
      collection(db, COLLECTION_NAME), 
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    
    // Apply limit if specified
    if (limitCount) {
      q = query(q, limit(limitCount));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to Date objects
        startDate: data.startDate ? data.startDate.toDate() : new Date(),
        expectedEndDate: data.expectedEndDate ? data.expectedEndDate.toDate() : undefined,
        actualEndDate: data.actualEndDate ? data.actualEndDate.toDate() : undefined,
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt ? data.updatedAt.toDate() : new Date()
      } as Order;
    });
  } catch (error) {
    console.error(`Error getting orders by status ${status}:`, error);
    throw error;
  }
};

/**
 * Get orders by CRM entry ID
 * @param crmEntryId CRM entry ID to filter by
 * @param limitCount Optional limit to the number of results
 * @returns Array of orders for the specified CRM entry
 */
export const getOrdersByCrmEntry = async (crmEntryId: string, limitCount?: number): Promise<Order[]> => {
  try {
    let q: Query | CollectionReference = query(
      collection(db, COLLECTION_NAME), 
      where('crmEntryId', '==', crmEntryId),
      orderBy('createdAt', 'desc')
    );
    
    // Apply limit if specified
    if (limitCount) {
      q = query(q, limit(limitCount));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to Date objects
        startDate: data.startDate ? data.startDate.toDate() : new Date(),
        expectedEndDate: data.expectedEndDate ? data.expectedEndDate.toDate() : undefined,
        actualEndDate: data.actualEndDate ? data.actualEndDate.toDate() : undefined,
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt ? data.updatedAt.toDate() : new Date()
      } as Order;
    });
  } catch (error) {
    console.error(`Error getting orders for CRM entry ${crmEntryId}:`, error);
    throw error;
  }
};

/**
 * Update an order in Firestore
 * @param id Order ID
 * @param orderData Order data to update
 * @returns Promise that resolves when the update is complete
 */
export const updateOrder = async (id: string, orderData: Partial<Omit<Order, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
  try {
    const orderRef = doc(db, COLLECTION_NAME, id);
    
    // Convert Date objects to Firestore Timestamps
    const dataWithTimestamps = {
      ...orderData,
      startDate: orderData.startDate ? Timestamp.fromDate(orderData.startDate) : null,
      expectedEndDate: orderData.expectedEndDate ? Timestamp.fromDate(orderData.expectedEndDate) : null,
      actualEndDate: orderData.actualEndDate ? Timestamp.fromDate(orderData.actualEndDate) : null,
      updatedAt: serverTimestamp()
    };

    await updateDoc(orderRef, dataWithTimestamps);
  } catch (error) {
    console.error('Error updating order:', error);
    throw error;
  }
};

/**
 * Delete an order from Firestore
 * @param id Order ID
 * @returns Promise that resolves when the deletion is complete
 */
export const deleteOrder = async (id: string): Promise<void> => {
  try {
    const orderRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(orderRef);
  } catch (error) {
    console.error('Error deleting order:', error);
    throw error;
  }
}; 